import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { FlagIcon } from '@heroicons/react/24/outline'

/** Ids wiring the confirmation's heading and body to its `aria-labelledby` / `aria-describedby`. */
const GOAL_DELETE_TITLE_ID = 'coach-goal-delete-title'
const GOAL_DELETE_BODY_ID = 'coach-goal-delete-body'

import { GoalScope, GoalStatus, type UserGoal } from '../../../../../../../pure-js/types/CoachTypes'
import { MAX_USER_GOALS, resolveGoalScope } from '../../../../../../../pure-js/libs/AICoachStateHelper'
import { toError } from '../../../../../../../pure-js/libs/Common'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import { TESTIDS } from '../../../../../config/testids'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { Button } from '../../../../../interaxDesignSystemComponents'
import { DefaultModal } from '../../../../../shared/components'
import { useDeleteUserGoal, useUpdateUserGoal } from '../../../../../apiLayer/queryHooks/coach'
import { GOAL_LIST_EMPTY_KEYS, GOAL_LIST_HEADING_KEYS } from '../coachPageStyles'
import { CoachGoalRow } from './CoachGoalRow'

/** What `updateUserGoal` answers when reactivating would pass the cap — `CoachErrorCode.TOO_MANY_GOALS`. */
const TOO_MANY_GOALS_CODE = 'too-many-goals'

/** The cap is a rule, not a fault, so "check your connection and try again" is advice that cannot work. */
const resolveGoalFailureKey = (error: unknown): string =>
  toError(error).message.includes(TOO_MANY_GOALS_CODE)
    ? 'gamer.ariaCoach.goalReopenBlocked'
    : 'gamer.ariaCoach.goalChangeFailed'

type CoachGoalsPanelProps = {
  goals: UserGoal[]
  /** Still fetching. Says nothing rather than asserting the list is empty before it is known. */
  isLoading?: boolean
  /** Rendered opposite the heading — the panel's fold control lives here rather than on a line of its own. */
  headerAction?: ReactNode
  /** Which of the two lists is shown; a leader's other list belongs to the other coaching focus. */
  goalScope?: GoalScope
}

export const CoachGoalsPanel = ({
  goals,
  isLoading = false,
  headerAction,
  goalScope = GoalScope.INDIVIDUAL
}: CoachGoalsPanelProps) => {
  const { t } = useTranslation()
  const updateMutation = useUpdateUserGoal()
  const deleteMutation = useDeleteUserGoal()
  const [goalPendingDeletion, setGoalPendingDeletion] = useState<UserGoal | undefined>(undefined)
  // Pinned to the goal it came from, so the reason sits on the row that was clicked.
  const [failure, setFailure] = useState<{ goalId: string; messageKey: string } | undefined>(undefined)

  const isBusy = updateMutation.isPending || deleteMutation.isPending

  // Shown to the person, not sent to Slack: the backend already classifies the expected rejections so
  // they page nobody, and reports the genuine failures itself.
  const handleSave = useCallback(
    (goal: UserGoal, text: string) => {
      setFailure(undefined)
      updateMutation
        .mutateAsync({ goalId: goal.id, goal: text })
        .catch((error) => setFailure({ goalId: goal.id, messageKey: resolveGoalFailureKey(error) }))
    },
    [updateMutation]
  )

  // Reactivating is refused when three are already active, so the failure line has to be able to show.
  const handleSetStatus = useCallback(
    (goal: UserGoal, status: GoalStatus) => {
      setFailure(undefined)
      updateMutation
        .mutateAsync({ goalId: goal.id, status })
        .catch((error) => setFailure({ goalId: goal.id, messageKey: resolveGoalFailureKey(error) }))
    },
    [updateMutation]
  )

  const closeDeleteConfirmation = useCallback(() => setGoalPendingDeletion(undefined), [])

  // The confirmation is a portal with no focus trap, so opening it has to move focus into the dialog.
  const cancelDeletionRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!goalPendingDeletion) return
    cancelDeletionRef.current?.querySelector('button')?.focus()
  }, [goalPendingDeletion])

  // Removing a goal is not undoable, so the confirmation names the goal rather than asking "are you sure".
  const confirmDeletion = useCallback(() => {
    if (!goalPendingDeletion) return
    const goalId = goalPendingDeletion.id
    setGoalPendingDeletion(undefined)
    setFailure(undefined)
    deleteMutation
      .mutateAsync({ goalId })
      .catch((error) => setFailure({ goalId, messageKey: resolveGoalFailureKey(error) }))
  }, [deleteMutation, goalPendingDeletion])

  const shownGoals = useMemo(() => goals.filter((goal) => resolveGoalScope(goal) === goalScope), [goals, goalScope])

  // Only once the answer is known, or the panel flashes "no goals yet" at someone who has three.
  const isEmptyListShown = shownGoals.length === 0 && !isLoading

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <FlagIcon width={20} color={INTERAX_COLORS.sun} />
          <Typography sx={{ color: INTERAX_COLORS.white, fontWeight: 700 }}>
            {t(GOAL_LIST_HEADING_KEYS[goalScope])}
          </Typography>
        </Stack>
        {headerAction}
      </Stack>

      {isEmptyListShown && (
        <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: 14 }}>
          {t(GOAL_LIST_EMPTY_KEYS[goalScope])}
        </Typography>
      )}

      {shownGoals.map((goal) => (
        <CoachGoalRow
          key={goal.id}
          goal={goal}
          disabled={isBusy}
          onSave={handleSave}
          onRequestDelete={setGoalPendingDeletion}
          onSetStatus={handleSetStatus}
          failureMessage={failure?.goalId === goal.id ? t(failure.messageKey, { max: MAX_USER_GOALS }) : undefined}
        />
      ))}

      <DefaultModal isOpen={Boolean(goalPendingDeletion)} onClose={closeDeleteConfirmation} maxWidth="420px">
        {/* `DefaultModal` is a plain portal with no role or focus handling of its own. */}
        <Stack
          spacing={2}
          role="alertdialog"
          aria-modal
          aria-labelledby={GOAL_DELETE_TITLE_ID}
          aria-describedby={GOAL_DELETE_BODY_ID}
        >
          <Typography id={GOAL_DELETE_TITLE_ID} sx={{ color: INTERAX_COLORS.white, fontWeight: 700, fontSize: 18 }}>
            {t('gamer.ariaCoach.goalDeleteTitle')}
          </Typography>
          <Typography id={GOAL_DELETE_BODY_ID} sx={{ color: INTERAX_COLORS.white70, fontSize: 14 }}>
            {t('gamer.ariaCoach.goalDeleteBody', { goal: goalPendingDeletion?.goal ?? '' })}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row', md: 'row' }} spacing={1.5} justifyContent="flex-end">
            {/* Keeping, not removing, is where focus lands on a destructive confirmation. */}
            <Box ref={cancelDeletionRef}>
              <Button variant="secondary" size="small" onClick={closeDeleteConfirmation}>
                {t('gamer.ariaCoach.goalDeleteCancel')}
              </Button>
            </Box>
            <Button
              variant="primary"
              size="small"
              onClick={confirmDeletion}
              data-testid={TESTIDS.COACH_GOAL_DELETE_CONFIRM_BTN}
            >
              {t('gamer.ariaCoach.goalDeleteConfirm')}
            </Button>
          </Stack>
        </Stack>
      </DefaultModal>
    </Stack>
  )
}
